import React, { useState, ReactNode, JSX } from 'react';
import { ApiBookingPane } from '@ibe/api';
import { faInfo, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import fallback from '../../../../_Translations/generated/booking-list.en.t.json';
import Keys from '../../../../_Translations/generated/booking-list.en.t.json.keys';
import useTranslation from '../../../Hooks/useTranslation';
import DateDisplay from '../../UI/DateDisplay/DateDisplay';
import { Message, MessageClass } from '../../UI/Messages/Message';
import CustomPagination from '../../CustomPagination/CustomPagination';
import BookingListUtil, { BookingPaneSortKeys, SortingOrder } from './BookingListUtil';

export interface BookingListProps {
  bookingList: ApiBookingPane[] | undefined;
  handleBookingPaneClick?: (bookingId: string | undefined) => void;
  customBookingRecordRenderer?: (bookingPane: ApiBookingPane) => ReactNode;
  showLeadTravelName?: boolean;
}

const BookingList = (props: BookingListProps): JSX.Element => {
  const { t } = useTranslation('booking-list', fallback, Keys);
  const {
    bookingList,
    handleBookingPaneClick,
    customBookingRecordRenderer,
    showLeadTravelName
  } = props;

  const [amountToDisplay, setAmountToDisplay] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [index, setIndex] = useState<number>(0);

  const [key, setKey] = useState<BookingPaneSortKeys | null>(null);
  const [order, setOrder] = useState<SortingOrder | null>(null);

  const lastPageIndex = bookingList
    ? Math.ceil((bookingList.length as number) / amountToDisplay)
    : 1;

  function changeOrder(): void {
    if (order === null) {
      setOrder(SortingOrder.ASC);
    } else if (order === SortingOrder.ASC) {
      setOrder(SortingOrder.DES);
    } else {
      setOrder(null);
      setKey(null);
    }
  }

  function changeKey(value: BookingPaneSortKeys): void {
    if (!key) {
      setKey(value);
      changeOrder();
    } else if (value === key) {
      changeOrder();
    } else {
      setOrder(SortingOrder.ASC);
      setKey(value);
    }
  }

  function handleSortingChange(value: BookingPaneSortKeys): void {
    changeKey(value);
  }

  function getItemsBasedOnPagination(items: ApiBookingPane[]): ApiBookingPane[] {
    const start = index;
    const end = index + amountToDisplay;
    return items.slice(start, end);
  }

  function getBookingsToShow(): ApiBookingPane[] | undefined {
    if (bookingList) {
      let result;
      if (key != null && order != null) {
        const copiedBookings = [...bookingList];
        result = BookingListUtil.sortItems(key, order, copiedBookings);
      } else {
        result = [...bookingList];
      }
      return getItemsBasedOnPagination(result);
    }
    return [];
  }

  function onFirst(): void {
    setCurrentPage(1);
    setIndex(0);
  }

  function onLast(): void {
    setCurrentPage(lastPageIndex);
    setIndex((lastPageIndex - 1) * amountToDisplay);
  }

  function onNext(): void {
    const targetStart = index + amountToDisplay;
    if (bookingList && targetStart < bookingList.length) {
      setCurrentPage(currentPage + 1);
      setIndex(targetStart);
    }
  }

  function onPrev(): void {
    const targetStart = index - amountToDisplay;
    if (targetStart >= 0) {
      setCurrentPage(currentPage - 1);
      setIndex(targetStart);
    }
  }

  function onPageNumberChange(pageNumber: number): void {
    const targetStart = (pageNumber - 1) * amountToDisplay;
    setCurrentPage(pageNumber);
    setIndex(targetStart);
  }

  function getSortingIcon(conectedKey: BookingPaneSortKeys): JSX.Element | null {
    if (key === conectedKey) {
      if (order === SortingOrder.ASC) {
        return <FontAwesomeIcon icon={faSortUp} />;
      }
      if (order === SortingOrder.DES) {
        return <FontAwesomeIcon icon={faSortDown} />;
      }
    }
    return null;
  }

  function getAgentNameInfo(bookingPane: ApiBookingPane): string {
    if (bookingPane.agent?.lastName && bookingPane.agent?.firstName) {
      return `${bookingPane.agent?.lastName} ${bookingPane.agent?.firstName}`;
    }
    return '-/-';
  }

  const bookings = getBookingsToShow()?.map((bookingPane: ApiBookingPane) => {
    if (bookingPane.bookingDate !== undefined && bookingPane.travelStartDate !== undefined) {
      if (customBookingRecordRenderer) {
        return customBookingRecordRenderer(bookingPane);
      } else
        return (
          <tbody key={bookingPane.bookingNumber}>
            <tr
              className="iso__bookingList__row"
              onClick={
                handleBookingPaneClick
                  ? (): void => handleBookingPaneClick(bookingPane.bookingId)
                  : undefined
              }
            >
              <td>{bookingPane.bookingNumber}</td>
              <td>{t(`${bookingPane.bookingState}`)}</td>
              <td>
                <DateDisplay date={bookingPane.bookingDate} />
              </td>
              <td>
                <DateDisplay date={bookingPane.travelStartDate} />
              </td>
              <td>
                {bookingPane.price?.finalPrice} {bookingPane.price?.currencyCode}
              </td>
              <td>{getAgentNameInfo(bookingPane)}</td>
              {showLeadTravelName && <td>{bookingPane.leadTravelerName}</td>}
            </tr>
          </tbody>
        );
    }
    return undefined;
  });

  if (bookings?.length === 0) {
    return (
      <Message message={t(Keys.warningMessage)} icon={faInfo} messageClass={MessageClass.INFO} />
    );
  }
  return (
    <div>
      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr className="iso__bookingList-header__row">
              <th
                scope="col"
                onClick={(e): void => {
                  e.preventDefault();
                  handleSortingChange(BookingPaneSortKeys.BOOKINGNUMBER);
                }}
              >
                {t(Keys.bookingnumber)} {getSortingIcon(BookingPaneSortKeys.BOOKINGNUMBER)}
              </th>
              <th
                scope="col"
                onClick={(): void => handleSortingChange(BookingPaneSortKeys.BOOKINGSTATE)}
              >
                {t(Keys.bookingstate)} {getSortingIcon(BookingPaneSortKeys.BOOKINGSTATE)}
              </th>
              <th
                scope="col"
                onClick={(): void => handleSortingChange(BookingPaneSortKeys.BOOKINGDATE)}
              >
                {t(Keys.bookingdate)} {getSortingIcon(BookingPaneSortKeys.BOOKINGDATE)}
              </th>
              <th
                scope="col"
                onClick={(): void => handleSortingChange(BookingPaneSortKeys.TRAVELSTARTDATE)}
              >
                {t(Keys.travelstart)} {getSortingIcon(BookingPaneSortKeys.TRAVELSTARTDATE)}
              </th>
              <th scope="col" onClick={(): void => handleSortingChange(BookingPaneSortKeys.PRICE)}>
                {t(Keys.amount)} {getSortingIcon(BookingPaneSortKeys.PRICE)}
              </th>
              <th scope="col" onClick={(): void => handleSortingChange(BookingPaneSortKeys.AGENT)}>
                {t(Keys.agent)} {getSortingIcon(BookingPaneSortKeys.AGENT)}
              </th>
              {showLeadTravelName && (
                <th
                  scope="col"
                  onClick={(): void => handleSortingChange(BookingPaneSortKeys.LEADTRAVELERNAME)}
                >
                  {t(Keys.leadTravelName)} {getSortingIcon(BookingPaneSortKeys.LEADTRAVELERNAME)}
                </th>
              )}
            </tr>
          </thead>
          {bookings}
        </table>
      </div>
      {bookings?.length && (
        <Pagination
          bookingList={bookingList}
          amountToDisplay={amountToDisplay}
          currentPage={currentPage}
          setAmountToDisplay={setAmountToDisplay}
          setCurrentPage={setCurrentPage}
          onPageNumberChange={onPageNumberChange}
          index={index}
          onNext={onNext}
          onLast={onLast}
          onFirst={onFirst}
          onPrev={onPrev}
        />
      )}
    </div>
  );
};

export default BookingList;

interface PaginationProps {
  bookingList?: ApiBookingPane[];
  amountToDisplay: number;
  currentPage: number;
  setAmountToDisplay: (a: number) => void;
  setCurrentPage: (a: number) => void;
  onPageNumberChange: (a: number) => void;

  index: number;
  onNext: () => void;
  onLast: () => void;
  onFirst: () => void;
  onPrev: () => void;
}
const Pagination = ({
  currentPage,
  amountToDisplay,
  bookingList,
  index,
  onFirst,
  onLast,
  onNext,
  onPageNumberChange,
  onPrev,
  setAmountToDisplay,
  setCurrentPage
}: PaginationProps): JSX.Element => {
  return bookingList ? (
    <CustomPagination
      onAmountChange={(amount: number): void => {
        if (amount - amountToDisplay !== 0) {
          setAmountToDisplay(amount);
          setCurrentPage(Math.ceil((index + 1) / amount));
        }
      }}
      amount={amountToDisplay}
      currentPage={currentPage}
      index={index}
      onFirst={(): void => onFirst()}
      onLast={(): void => onLast()}
      onNext={(): void => onNext()}
      onPageNumber={(pageNumber: number): void => onPageNumberChange(pageNumber)}
      onPrev={(): void => onPrev()}
      totalCount={bookingList.length}
    />
  ) : (
    <div />
  );
};
